'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';

interface Heading {
  key: string;
  text: string;
  level: number;
  id: string;
}

export function ArticleToc({
  headings,
  tocTitle = 'In this article',
  promoHeadline = 'Reach your customers',
  promoSubtitle = 'Build your first agent in minutes.',
  promoCta = 'Get started free',
}: {
  headings: Heading[];
  tocTitle?: string;
  promoHeadline?: string;
  promoSubtitle?: string;
  promoCta?: string;
}) {
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
    );

    const elements = headings
      .map((h) => document.getElementById(h.id))
      .filter(Boolean) as HTMLElement[];

    for (const el of elements) observer.observe(el);
    return () => observer.disconnect();
  }, [headings]);

  if (headings.length === 0) return null;

  return (
    <div className="border-l-4 border-emerald-500">
      {/* TOC */}
      <nav className="pl-5">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {tocTitle}
        </h2>
        <ul className="space-y-2">
          {headings.map((h) => (
            <li key={h.key}>
              <a
                href={`#${h.id}`}
                className={`block text-sm leading-snug transition-colors ${
                  activeId === h.id
                    ? 'font-medium text-emerald-600'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {h.text}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {/* Promo card — connected via the same left border */}
      <div className="mt-6 bg-emerald-500 p-5">
        <p className="text-lg text-white">
          {promoHeadline}
        </p>
        <p className="mt-1.5 text-sm text-emerald-100">
          {promoSubtitle}
        </p>
        <Link
          href="/"
          className="mt-4 inline-flex items-center gap-1.5 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
        >
          {promoCta}
          <span aria-hidden>&rarr;</span>
        </Link>
      </div>
    </div>
  );
}
